import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { MODELS, MODEL_LABEL, PH } from "./priceUtils";

const toForm = (c) => ({ price: c?.na || c?.price === undefined || c?.price === null ? "" : String(c.price), note: c?.note || "", na: !!c?.na });
export const fromForm = (f) => ({ price: f.na || f.price.trim() === "" ? (f.na ? null : PH) : Number(f.price.replace(/[$,\s]/g, "")), note: f.note, na: f.na });
const badNum = (f) => !f.na && f.price.trim() !== "" && !/^\d+(\.\d+)?$/.test(f.price.replace(/[$,\s]/g, ""));

export function CellFields({ f, set, testid }) {
  return (
    <div className="grid grid-cols-[110px_1fr_auto] items-center gap-2">
      <Input value={f.price} disabled={f.na} onChange={(e) => set({ ...f, price: e.target.value })} placeholder="9999"
             className={badNum(f) ? "border-red-400" : ""} data-testid={`${testid}-price`} />
      <Input value={f.note} onChange={(e) => set({ ...f, note: e.target.value })} placeholder="Note (optional)" data-testid={`${testid}-note`} />
      <label className="flex items-center gap-1 text-xs text-slate-500">
        <Checkbox checked={f.na} onCheckedChange={(v) => set({ ...f, na: !!v })} data-testid={`${testid}-na`} /> N/A
      </label>
    </div>
  );
}

/** Edit one price cell (current list or a batch line). */
export function CellDialog({ target, onSave, onClose }) {
  const [f, setF] = useState(() => toForm(target?.cell));
  const [busy, setBusy] = useState(false);
  if (!target) return null;
  const save = async () => { setBusy(true); try { await onSave(fromForm(f)); onClose(); } finally { setBusy(false); } };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="price-cell-dialog">
        <DialogHeader>
          <DialogTitle>{MODEL_LABEL[target.model]} — {target.item.name}</DialogTitle>
          <DialogDescription>{target.batch ? `New price in "${target.batch.name}" (effective ${target.batch.effective_date}).`
            : "Leave the price empty for the $9,999 placeholder, or tick N/A if this model will never have a price."}</DialogDescription>
        </DialogHeader>
        <CellFields f={f} set={setF} testid="price-cell" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || badNum(f)} data-testid="price-cell-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Add a product (all three models) or edit an item's name / unit / category. */
export function ItemDialog({ item, categories, parents, onSave, onClose }) {
  const isNew = !item?.item_id;
  const [m, setM] = useState({ name: item?.name || "", unit: item?.unit || "", category: item?.category || categories[0] || "",
                               description: item?.description || "", parent_id: item?.parent_id || "" });
  const [cells, setCells] = useState(() => Object.fromEntries(MODELS.map((k) => [k, toForm(item?.prices?.[k])])));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await onSave({ ...m, parent_id: m.parent_id || null,
                     ...(isNew ? { prices: Object.fromEntries(MODELS.map((k) => [k, fromForm(cells[k])])) } : {}) });
      onClose();
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="price-item-dialog">
        <DialogHeader><DialogTitle>{isNew ? "Add product or service" : `Edit ${item.name}`}</DialogTitle>
          <DialogDescription>{isNew ? "New items are saved as drafts — publish them to show partners right away (no notice period)."
            : "Name, unit and category changes apply immediately. Price changes on live items go through a change batch."}</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Item or Service</Label><Input value={m.name} onChange={(e) => setM({ ...m, name: e.target.value })} data-testid="price-item-name" /></div>
          <div><Label>Unit</Label><Input value={m.unit} onChange={(e) => setM({ ...m, unit: e.target.value })} placeholder="each/month" data-testid="price-item-unit" /></div>
          <div><Label>Category</Label><Input list="price-cats" value={m.category} onChange={(e) => setM({ ...m, category: e.target.value })} data-testid="price-item-category" />
            <datalist id="price-cats">{categories.map((c) => <option key={c} value={c} />)}</datalist></div>
          <div className="col-span-2"><Label>Description</Label><Input value={m.description} onChange={(e) => setM({ ...m, description: e.target.value })} /></div>
          <div className="col-span-2"><Label>Included in (optional)</Label>
            <select value={m.parent_id} onChange={(e) => setM({ ...m, parent_id: e.target.value })} className="h-9 w-full rounded-md border border-slate-200 px-2 text-sm">
              <option value="">— Not an included feature —</option>{parents.map((p) => <option key={p.item_id} value={p.item_id}>{p.name}</option>)}
            </select></div>
        </div>
        {isNew && <div className="space-y-2">{MODELS.map((k) => (
          <div key={k}><Label className="text-xs">{MODEL_LABEL[k]}</Label>
            <CellFields f={cells[k]} set={(v) => setCells({ ...cells, [k]: v })} testid={`price-item-${k}`} /></div>))}</div>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || !m.name.trim() || (isNew && MODELS.some((k) => badNum(cells[k])))} data-testid="price-item-save">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {isNew ? "Add as draft" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
